import React from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Catches a crash inside one module screen.
 *
 * Without this, a single bad render takes the whole React tree down: the menu
 * still navigates and the address bar still changes, but the page goes blank
 * with nothing to say why. A blank screen is the worst kind of error report —
 * it looks like the menu is broken when the fault is in one module.
 *
 * The boundary is keyed by module, so opening a different one from the menu
 * clears the error and gives that screen a fresh mount.
 */
export interface ModuleErrorBoundaryProps {
  /** Module id, used to reset when the user navigates elsewhere. */
  moduleId: string;
  isDarkMode?: boolean;
  children?: ReactNode;
}

interface State {
  error: Error | null;
}

class ModuleErrorBoundary extends React.Component<ModuleErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the console so the stack is there to copy when reporting it.
    // eslint-disable-next-line no-console
    console.error(`[${this.props.moduleId}] failed to render`, error, info.componentStack);
  }

  componentDidUpdate(prev: ModuleErrorBoundaryProps) {
    if (prev.moduleId !== this.props.moduleId && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const dark = this.props.isDarkMode;
    return (
      <div
        role="alert"
        style={{
          margin: '24px auto', maxWidth: '720px', padding: '22px 24px',
          borderRadius: '10px',
          border: `1px solid ${dark ? '#5C2B2B' : '#F0C5C5'}`,
          backgroundColor: dark ? '#2A1A1A' : '#FDF3F3',
          color: dark ? '#F3DADA' : '#7A1F1F'
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: '17px' }}>
          {this.props.moduleId} could not be opened
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: '14px', lineHeight: 1.6 }}>
          Something in this screen stopped it from loading. The rest of the
          application is unaffected — pick another item from the menu, or reload
          the page to try again.
        </p>
        <pre
          style={{
            margin: 0, padding: '10px 12px', borderRadius: '6px', overflowX: 'auto',
            fontSize: '12.5px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            backgroundColor: dark ? '#1C1212' : '#FFFFFF',
            border: `1px solid ${dark ? '#4A2323' : '#EFD3D3'}`
          }}
        >
          {error.message || String(error)}
        </pre>
      </div>
    );
  }
}

export default ModuleErrorBoundary;
